import { SocketClient } from "cbcore-ts"
import { UIColor, UIRoute, UITextView, UIView, UIViewController } from "uicore-ts"
import { CBDialogViewShower } from "./Custom components/CBDialogViewShower"
import { RHButton } from "./Custom components/RH/RHButton"
import { RHColor } from "./Custom components/RH/RHColor"
import { NewProcurementViewController } from "./NewProcurementViewController"
import { ProcurerMainViewController } from "./ProcurerMainViewController"

export class NewProcurementPreviewViewController extends UIViewController {
    
    // Header
    readonly titleLabel: UITextView = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header2
    ).configuredWithObject({
        text: "Hanke eelvaade",
        hoverText: "",
        backgroundColor: UIColor.transparentColor
    }).addedAsSubviewToView(this.view)
    
    // #region Detail Labels
    
    // Helper to create bold header labels
    private createHeaderLabel(text: string): UITextView {
        return new UITextView(undefined, UITextView.type.header3)
            .configuredWithObject({
                text: text + ": "
            })
            .addedAsSubviewToView(this.view)
    }
    
    // Helper to create value labels
    private createValueLabel(): UITextView {
        return new UITextView()
            .configuredWithObject({ text: "-" })
            .addedAsSubviewToView(this.view)
    }
    
    // Type
    typeHeaderLabel = this.createHeaderLabel("Hanke liik")
    typeValueLabel = this.createValueLabel()
    
    // Title
    procurementTitleHeaderLabel = this.createHeaderLabel("Hanke pealkiri")
    procurementTitleValueLabel = this.createValueLabel()
    
    // Description
    descriptionHeaderLabel = this.createHeaderLabel("Kirjeldus")
    descriptionValueLabel = this.createValueLabel()
    
    // CPV
    cpvHeaderLabel = this.createHeaderLabel("CPV kood")
    cpvValueLabel = this.createValueLabel()
    
    // Cost
    costHeaderLabel = this.createHeaderLabel("Eeldatav maksumus")
    costValueLabel = this.createValueLabel()
    
    // Contract duration
    contractDurationHeaderLabel = this.createHeaderLabel("Hankelepingu kestus")
    contractDurationValueLabel = this.createValueLabel()
    
    // Deadline
    deadlineHeaderLabel = this.createHeaderLabel("Esitamise tähtaeg")
    deadlineValueLabel = this.createValueLabel()
    
    // Auto-extension details
    extensionHeaderLabel = this.createHeaderLabel("Automaatne pikendamine")
    extensionValueLabel = this.createValueLabel()
    
    // Parts
    partsHeaderLabel = this.createHeaderLabel("Hankeosad")
    partsValueLabel = this.createValueLabel()
    
    // #endregion
    
    
    editProcurementButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Muuda hanget veel"
        },
        controlEventTargetAccumulator: {
            // @ts-ignore
            PointerUpInside: {
                EnterDown: async (sender, event) => {
                    
                    
                    UIRoute.currentRoute
                        .routeByRemovingComponentNamed(NewProcurementPreviewViewController.routeComponentName)
                        .routeWithViewControllerComponent(
                            NewProcurementViewController,
                            {
                                id: UIRoute.currentRoute.componentWithViewController(
                                    NewProcurementPreviewViewController
                                )!.parameters.id
                            }
                        ).apply()
                    
                }
            }
        }
    })
    
    publishProcurementButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Avalda hange"
        },
        controlEventTargetAccumulator: {
            // @ts-ignore
            PointerUpInside: {
                EnterDown: async (sender, event) => {
                    
                    // Here you might want to change status from DRAFT to PUBLISHED in backend
                    CBDialogViewShower.alert("Hange on nüüd avaldatud (tegelt mitte).")
                    
                    UIRoute.currentRoute
                        .routeByRemovingComponentNamed(NewProcurementPreviewViewController.routeComponentName)
                        .routeWithViewControllerComponent(ProcurerMainViewController, {})
                        .apply()
                    
                }
            }
        }
    })
    
    bottomView = new UIView().addedAsSubviewToView(this.view)
    
    
    constructor(view: UIView) {
        super(view)
        
        this.view.configureWithObject({
            backgroundColor: RHColor.backgroundColor
        })
    }
    
    
    static override readonly routeComponentName = "new_procurement_preview"
    static override readonly ParameterIdentifierName = {
        "id": "id"
    }
    
    
    async populateData() {
        
        const procurementID = UIRoute.currentRoute.componentWithViewController(
            NewProcurementPreviewViewController)?.parameters.id
        if (!procurementID) {
            this.procurementTitleValueLabel.text = "(Viga: Hange täpsustamata)"
            return
        }
        
        const data = (await SocketClient.RetrieveProcurementPrivateInformation(
            { id: procurementID }
        )).result
        
        if (!data) {
            this.procurementTitleValueLabel.text = "(Andmed puuduvad)"
            return
        }
        
        // 1. Type Mapping
        const typeMap: Record<string, string> = {
            "newProcurement": "Hange hankelepingu sõlmimiseks",
            "frameProcurement": "Hange raamlepingu sõlmimiseks",
            "tiedProcurement": "Seotud hange",
            "copyProcurement": "Kopeeritud hange"
        }
        this.typeValueLabel.text = (data.userSelectedFlow && typeMap[data.userSelectedFlow])
                                   ? typeMap[data.userSelectedFlow]
                                   : (data.userSelectedFlow || "-")
        
        // 2. Basic Text Fields
        this.procurementTitleValueLabel.text = data.title || "-"
        this.descriptionValueLabel.text = data.description || "-"
        this.cpvValueLabel.text = data.CPV || "-"
        
        // 3. Cost
        this.costValueLabel.text = data.estimatedCost
                                   ? `${data.estimatedCost} €`
                                   : "-"
        
        // 4. Date
        if (data.deadlineDate) {
            // Formatting date to DD.MM.YYYY HH:mm
            const d = new Date(data.deadlineDate)
            const dateStr = d.toLocaleDateString("et-EE")
            const timeStr = d.toLocaleTimeString("et-EE", { hour: "2-digit", minute: "2-digit" })
            this.deadlineValueLabel.text = `${dateStr} ${timeStr}`
        }
        else {
            this.deadlineValueLabel.text = "-"
        }
        
        // 5. Extension Logic
        if (data.minimumAcceptableNumberOfOffers !== null && (data.minimumAcceptableNumberOfOffers || 0) > 0) {
            this.extensionValueLabel.text = `Kui alla ${
                data.minimumAcceptableNumberOfOffers
            } pakkumuse, pikeneb ${
                data.numberOfDaysToExtendDeadlineByIfTooFewOffers
            } päeva.`
        }
        else {
            this.extensionValueLabel.text = "Ei rakendu"
        }
        
        // 6. Parts
        // Logic from previous file: parts: this.hankeosadCheckbox.selected ? [] : null
        this.partsValueLabel.text = (data.parts !== null) ? "Jah, jaotatud osadeks" : "Ei"
        
        // Trigger a layout update if text sizes changed significantly
        this.view.setNeedsLayout()
    }
    
    override async viewDidAppear() {
        super.viewDidAppear()
        // Refresh data every time the view appears
        this.populateData()
    }
    
    // #region Layout Logic
    
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const minRowHeight = padding * 1.5
        const bounds = this.view.bounds.rectangleWithInset(padding)
        
        // Always place title at top
        this.titleLabel.frame = bounds.rectangleWithHeight(minRowHeight * 2)
        
        const contentStartY = this.titleLabel.frame.y + this.titleLabel.frame.height + padding
        const contentRect = bounds.rectangleWithY(contentStartY).rectangleWithHeight(bounds.height - contentStartY)
        
        let bottomOfContent = 0
        
        // Threshold check: 850 * 2 = 1700px for two columns
        if (bounds.width >= 1700) {
            bottomOfContent = this.layoutTwoColumns(contentRect, padding, minRowHeight)
        }
        else {
            bottomOfContent = this.layoutSingleColumn(contentRect, padding, minRowHeight)
        }
        
        // Place Publish Button below the content
        this.view.bounds
            .rectangleWithInset(padding)
            .rectangleWithY(bottomOfContent + padding * 2)
            .rectangleWithHeight(minRowHeight * 2)
            .distributeViewsEquallyAlongWidth([this.editProcurementButton, this.publishProcurementButton], padding)
        
        this.bottomView.frame = this.publishProcurementButton.frame.rectangleForNextRow(padding, padding)
        
    }
    
    
    // Getter to easily iterate over pairs in order
    private get dataPairs() {
        return [
            { header: this.typeHeaderLabel, value: this.typeValueLabel },
            { header: this.procurementTitleHeaderLabel, value: this.procurementTitleValueLabel },
            { header: this.descriptionHeaderLabel, value: this.descriptionValueLabel },
            { header: this.cpvHeaderLabel, value: this.cpvValueLabel },
            { header: this.contractDurationHeaderLabel, value: this.contractDurationValueLabel },
            { header: this.costHeaderLabel, value: this.costValueLabel },
            { header: this.deadlineHeaderLabel, value: this.deadlineValueLabel },
            { header: this.extensionHeaderLabel, value: this.extensionValueLabel },
            { header: this.partsHeaderLabel, value: this.partsValueLabel }
        ]
    }
    
    /**
     * Subroutine for Narrower Screens (Standard and Mobile)
     */
    private layoutSingleColumn(bounds: any, padding: number, minRowHeight: number): number {
        let currentFrame = bounds.rectangleWithHeight(0)
        
        this.dataPairs.forEach(pair => {
            currentFrame = this.layoutPair(pair, currentFrame, bounds.width, padding, minRowHeight)
        })
        
        return currentFrame.max.y
    }
    
    /**
     * Subroutine for Wide Screens
     */
    private layoutTwoColumns(bounds: any, padding: number, minRowHeight: number): number {
        const allPairs = this.dataPairs
        const halfIndex = Math.ceil(allPairs.length / 2)
        
        const leftPairs = allPairs.slice(0, halfIndex)
        const rightPairs = allPairs.slice(halfIndex)
        
        const columnWidth = (bounds.width - padding) / 2
        
        // Left Column
        let currentLeftFrame = bounds.rectangleWithWidth(columnWidth).rectangleWithHeight(0)
        leftPairs.forEach(pair => {
            currentLeftFrame = this.layoutPair(pair, currentLeftFrame, columnWidth, padding, minRowHeight)
        })
        
        // Right Column
        let currentRightFrame = bounds
            .rectangleWithX(bounds.x + columnWidth + padding)
            .rectangleWithWidth(columnWidth)
            .rectangleWithHeight(0)
        
        rightPairs.forEach(pair => {
            currentRightFrame = this.layoutPair(pair, currentRightFrame, columnWidth, padding, minRowHeight)
        })
        
        return Math.max(currentLeftFrame.max.y, currentRightFrame.max.y)
    }
    
    /**
     * Dynamic Pair Layout:
     * - Width < 500: Stacked (Value under Header)
     * - Width >= 500: Side-by-Side (Header left, Value right)
     */
    private layoutPair(
        pair: { header: UITextView, value: UITextView },
        previousFrame: any,
        containerWidth: number,
        padding: number,
        minRowHeight: number
    ): any {
        
        const startY = (previousFrame.height === 0) ? previousFrame.y : (previousFrame.max.y + padding * 0.5)
        
        // MOBILE MODE: Stacked
        if (containerWidth < 520) {
            const headerHeight = pair.header.intrinsicContentHeight(containerWidth)
            pair.header.frame = previousFrame.rectangleWithY(startY).rectangleWithHeight(headerHeight)
                .rectangleWithWidth(containerWidth)
            
            const valueHeight = pair.value.intrinsicContentHeight(containerWidth)
            pair.value.frame = pair.header.frame.rectangleForNextRow(
                padding * 0.25, Math.max(valueHeight, minRowHeight))
            
            pair.header.textAlignment = UITextView.textAlignment.left
            
            return pair.value.frame
        }
        
        // TABLET/DESKTOP MODE: Side-by-Side
        const totalWeight = 3
        const effectiveWidth = containerWidth - padding
        
        const headerWidth = effectiveWidth * (1 / totalWeight)
        const valueWidth = effectiveWidth * (2 / totalWeight)
        
        const headerHeight = pair.header.intrinsicContentHeight(headerWidth)
        const valueHeight = pair.value.intrinsicContentHeight(valueWidth)
        
        const rowHeight = Math.max(headerHeight, valueHeight, minRowHeight)
        
        const rowFrame = previousFrame
            .rectangleWithY(startY)
            .rectangleWithHeight(rowHeight)
            .rectangleWithWidth(containerWidth)
        
        rowFrame.distributeViewsAlongWidth([pair.header, pair.value], [1, 2], padding)
        
        pair.header.textAlignment = UITextView.textAlignment.right
        
        return rowFrame
    }
    
    // #endregion
    
    
    
}
